import { useRoute, useRouter } from 'vue-router'

import { routeQueryString } from './routeQuery'

/**
 * Whether `?back=` names a screen of this app.
 *
 * It arrives from the address bar, so it is checked rather than trusted. A value opening
 * `//` or `/\\` is a protocol-relative address: the router resolves it against the origin
 * and `history.pushState` then refuses it cross-origin, so Cancel dies with a
 * `SecurityError` instead of going anywhere. A whole URL is refused for the same reason —
 * there is no screen of ours behind `https://…`.
 */
const isInAppPath = (value: string) =>
  value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')

/**
 * Where a full-page form goes when it is done — Cancel and Save alike.
 *
 * A form page reached from several lists cannot name its own way out, so whoever linked to
 * it says where with `?back=`. Pass the screen that owns the record as the fallback — that
 * is where a typed URL or a link without the param lands.
 *
 * `router.push` rather than `back()`: the form has just changed what the previous
 * screen shows, and history may not even hold it — a page opened in a new tab has
 * nothing behind it.
 */
export function useBackTo(fallback: string) {
  const route = useRoute()
  const router = useRouter()

  function goBack() {
    // Read, not cast: `?back=a&back=b` makes this an array, and `router.push(array)`
    // throws rather than navigating anywhere.
    const back = routeQueryString(route.query.back)

    router.push(back && isInAppPath(back) ? back : fallback)
  }

  return { goBack }
}
